import { useMutation, useQuery } from "@apollo/client";
import { Breadcrumb, Button, Dropdown, Icon, Modal, Tag, Toggle } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { createKeywordGroup } from "../../graphql/mutations/createKeywordGroup";
import { deleteKeywordGroup } from "../../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../../graphql/mutations/updateKeywordGroup";
import { listProducts } from "../../graphql/queries/listProducts";
import { useProductGroups } from "../../hooks/useProductGroups";
import { useToast } from "../../hooks/useToast";
import AddKeywordGroupModal from "./AddKeywordGroupModal";
import { productFormSchema, productInfoUISchema } from "./productFormSchema";
import "./productManagement.scss";

const PRODUCT_TYPE = "PRODUCT";

const generateSlug = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const initialFormData = {
  name: "",
  slug: "",
  imageUrl: null,
  keywords: [],
  keywordsGroupIds: [],
  metadata: {
    ean_upc: "",
  },
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { toast, showToast, closeToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isFormReady, setIsFormReady] = useState(!isEditMode);

  // Active/Inactive toggle
  const [isActive, setIsActive] = useState(true);
  const [statusToggleKey, setStatusToggleKey] = useState(0);
  const [statusBlockedMessage, setStatusBlockedMessage] = useState("");

  // Product groups (PRODUCT_GROUP type)
  const { productGroups, productGroupMap } = useProductGroups();
  const [selectedProductGroups, setSelectedProductGroups] = useState([]);

  // Keyword groups stored locally: { groupId, groupName, keywords }
  const [keywordGroups, setKeywordGroups] = useState([]);
  const [showAddKeywordModal, setShowAddKeywordModal] = useState(false);
  const [editingKeywordGroup, setEditingKeywordGroup] = useState(null);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);

  // Fetch existing product data for edit mode
  const { data: productsData, loading: productsLoading } = useQuery(listProducts, {
    variables: { showDeleted: true },
    fetchPolicy: "network-only",
    skip: !isEditMode,
  });

  // Set form data from product on edit
  useEffect(() => {
    if (!isEditMode) {
      setFormData(initialFormData);
      setKeywordGroups([]);
      setSelectedProductGroups([]);
      setIsActive(true);
      setIsFormReady(true);
      return;
    }
    setIsFormReady(false);
    if (productsData?.listKeywordGroups) {
      const product = productsData.listKeywordGroups.find((p) => p.id === id);
      if (product) {
        const meta =
          typeof product.metadata === "string"
            ? JSON.parse(product.metadata)
            : product.metadata || {};
        setFormData({
          id: product.id,
          name: product.name || "",
          slug: product.slug || generateSlug(product.name || ""),
          imageUrl: product.imageUrl || null,
          keywords: product.keywords || [],
          keywordsGroupIds: product.keywordsGroupIds || [],
          metadata: {
            ean_upc: meta.ean_upc || "",
          },
        });
        setIsActive(!product.deleted);

        // Restore keyword groups from metadata
        const savedKeywordGroups = meta.keywordGroups || [];
        setKeywordGroups(
          savedKeywordGroups.map((kg) => ({
            groupId: kg.groupId,
            groupName: kg.groupName,
            keywords: kg.keywords || [],
          }))
        );

        setIsFormReady(true);
      }
    }
  }, [isEditMode, productsData, id]);

  // Once product data and product group map are loaded, set the selected product groups
  useEffect(() => {
    if (!isEditMode || !formData.keywordsGroupIds?.length) return;
    if (Object.keys(productGroupMap).length === 0) return;

    const selected = formData.keywordsGroupIds
      .filter((gid) => productGroupMap[gid])
      .map((gid) => ({ id: gid, displayText: productGroupMap[gid] }));

    setSelectedProductGroups(selected);
  }, [isEditMode, formData.keywordsGroupIds, productGroupMap]);

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        ...data,
        id: prev.id,
        keywordsGroupIds: prev.keywordsGroupIds,
        metadata: {
          ...prev.metadata,
          ...(data.metadata || {}),
        },
      };
      const currentName = data.name !== undefined ? data.name : prev.name;
      updated.slug = generateSlug(currentName);
      return updated;
    });
  }, []);

  const handleProductGroupChange = useCallback((selected) => {
    setSelectedProductGroups(selected || []);
  }, []);

  const handleStatusUpdate = useCallback(
    (checked) => {
      const product = productsData?.listKeywordGroups?.find(
        (item) => item.id === id
      );

      if (!checked && product?.isLinked) {
        const links = Array.isArray(product.linkedTo)
          ? product.linkedTo
          : [product.linkedTo];
        const attachedTo = [
          ...new Set(links.map((link) => link?.attachedTo).filter(Boolean)),
        ].join(", ");

        setStatusBlockedMessage(
          `Product "${product.name}" is attached to ${
            attachedTo || "another item"
          } and cannot be deactivated.`
        );
        // pepsico-ds Toggle maintains internal state, so remount it to restore
        // the controlled active value after rejecting the switch-off action.
        setStatusToggleKey((currentKey) => currentKey + 1);
        return;
      }

      setIsActive(checked);
    },
    [id, productsData]
  );

  const handleCancel = () => {
    navigate("/product-management");
  };

  const jsonFormsConfig = useMemo(() => ({ showValidation }), [showValidation]);

  // Keyword Group Handlers
  const handleAddKeywordGroup = () => {
    setEditingKeywordGroup(null);
    setShowAddKeywordModal(true);
  };

  const handleEditKeywordGroup = (keywordGroup) => {
    setEditingKeywordGroup(keywordGroup);
    setShowAddKeywordModal(true);
  };

  const handleDeleteKeywordGroup = (keywordGroup) => {
    setKeywordGroups((prev) =>
      prev.filter((kg) => kg.groupId !== keywordGroup.groupId)
    );
  };

  const handleSaveKeywordGroup = ({ groupId, groupName, keywords }) => {
    setKeywordGroups((prev) => {
      const existingIndex = prev.findIndex((kg) => kg.groupId === groupId);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { groupId, groupName, keywords };
        return updated;
      }
      // Add new
      return [...prev, { groupId, groupName, keywords }];
    });
    setShowAddKeywordModal(false);
    setEditingKeywordGroup(null);
  };

  // Compute whether all required fields are filled for save button state
  const hasKeywords = keywordGroups.some((kg) => (kg.keywords || []).length > 0);
  const isSaveDisabled = isSubmitting || !formData.name?.trim() || !hasKeywords;

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setShowValidation(true);
      showToast("error", "Validation Error", "Product name is required");
      return;
    }

    // Collect all keywords from all keyword groups
    const allKeywords = keywordGroups.reduce((acc, kg) => {
      return [...acc, ...(kg.keywords || [])];
    }, []);

    if (allKeywords.length === 0) {
      setShowValidation(true);
      showToast(
        "error",
        "Validation Error",
        "At least one keyword is required. Please add a keyword group with keywords."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Product group IDs from dropdown selection
      const productGroupIds = selectedProductGroups.map((g) => g.id);

      // Outlet group IDs from keyword groups
      const outletGroupIds = keywordGroups.map((kg) => kg.groupId);

      // Merge both into keywordsGroupIds
      const keywordsGroupIds = [...productGroupIds, ...outletGroupIds];

      const payload = {
        name: formData.name,
        slug: generateSlug(formData.name),
        imageUrl: formData.imageUrl || null,
        type: PRODUCT_TYPE,
        keywords: allKeywords,
        keywordsGroupIds,
        metadata: {
          ean_upc: formData.metadata.ean_upc,
          name: formData.name,
          keywordGroups: keywordGroups.map((kg) => ({
            groupId: kg.groupId,
            groupName: kg.groupName,
            keywords: kg.keywords,
          })),
        },
      };

      if (isEditMode && formData.id) {
        await executeUpdateKeyword({
          variables: { id: formData.id, input: payload },
        });

        // Handle active/inactive via deleteKeywordGroup (soft delete)
        const product = productsData?.listKeywordGroups?.find((p) => p.id === id);
        const wasActive = !product?.deleted;
        if (wasActive && !isActive) {
          // Deactivate: soft delete
          await executeDeleteKeyword({ variables: { id: formData.id } });
        }

        showToast(
          "success",
          "Product Updated",
          "Product has been updated successfully"
        );
      } else {
        const result = await executeCreateKeyword({
          variables: { input: payload },
        });

        // If creating as inactive, soft delete right after
        if (!isActive) {
          const newId = result?.data?.createKeywordGroup?.id;
          if (newId) {
            await executeDeleteKeyword({ variables: { id: newId } });
          }
        }

        showToast(
          "success",
          "Product Created",
          "Product has been created successfully"
        );
      }

      setIsSubmitting(false);
      setTimeout(() => navigate("/product-management"), 1200);
    } catch (error) {
      setIsSubmitting(false);
      showToast(
        "error",
        "Error",
        error.message || "An error occurred while saving the product"
      );
    }
  };

  const breadcrumbs = [
    { label: "Campaign elements", href: "/product-management" },
    { label: "Products", href: "/product-management" },
    {
      label: isEditMode ? formData.name || "Edit Product" : "New product",
      href: "#",
    },
  ];

  const MAX_VISIBLE_KEYWORDS = 10;

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb breadcrumbs={breadcrumbs} activeIndex={2} />

        {isEditMode && (productsLoading || !isFormReady) ? (
          <div className="product-detail-page">
            <p>Loading product data...</p>
          </div>
        ) : (
          <div className="product-detail-page">
            <div className="product-detail-section">
              <h3 className="section-title">Product information</h3>
              <JsonFormsWrapper
                jsonschema={productFormSchema}
                uischema={productInfoUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
              />
            </div>

            {/* Active/Inactive Toggle */}
            <div className="product-status-section">
              <Toggle
                key={statusToggleKey}
                checked={isActive}
                onUpdate={handleStatusUpdate}
                value="Status"
                // label={isActive ? "Active" : "Inactive"}
                size="medium"
              />
            </div>

            {/* Product Group Section */}
            <div className="product-group-section">
              <h3 className="section-title">Product Group</h3>
              <Dropdown
                childList={productGroups}
                selection="multiple"
                selectedValue={selectedProductGroups}
                setSelectedValue={handleProductGroupChange}
                placeholder="Select product groups"
                size="medium"
              />
            </div>

            {/* Keywords Section */}
            <div className="keywords-section">
              <h3 className="keywords-title">Keywords</h3>

              {keywordGroups.map((kg) => (
                <KeywordGroupCard
                  key={kg.groupId}
                  keywordGroup={kg}
                  maxVisible={MAX_VISIBLE_KEYWORDS}
                  onEdit={handleEditKeywordGroup}
                  onDelete={handleDeleteKeywordGroup}
                />
              ))}

              <div
                className="add-keyword-group-card"
                onClick={handleAddKeywordGroup}
              >
                <Icon icon="add_circle" className="add-icon" />
                <div className="add-text">
                  <h4>Add keywords group</h4>
                </div>
              </div>
            </div>

            <div className="product-detail-actions">
              <Button
                text="Cancel"
                variant="secondary"
                size="medium"
                onClick={handleCancel}
                disabled={isSubmitting}
              />
              <Button
                text="Save"
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={isSaveDisabled}
              />
            </div>
          </div>
        )}

        <ToastNotification {...toast} handleClose={closeToast} />

        <Modal
          showModal={Boolean(statusBlockedMessage)}
          title="Cannot Deactivate Product"
          text={statusBlockedMessage}
          onCloseModal={() => setStatusBlockedMessage("")}
          showPrimaryButton={false}
          secondaryButtonProps={{
            size: "medium",
            text: "Cancel",
            variant: "secondary",
            onClick: () => setStatusBlockedMessage(""),
          }}
          showTertiaryButton={false}
          showLink={false}
        />

        <AddKeywordGroupModal
          isOpen={showAddKeywordModal}
          onClose={() => {
            setShowAddKeywordModal(false);
            setEditingKeywordGroup(null);
          }}
          onSave={handleSaveKeywordGroup}
          attachedKeywordGroupIds={keywordGroups.map((kg) => kg.groupId)}
          editingGroup={editingKeywordGroup}
        />
      </div>
    </div>
  );
}

// Keyword Group Card sub-component
function KeywordGroupCard({ keywordGroup, maxVisible, onEdit, onDelete }) {
  const [showAll, setShowAll] = useState(false);
  const keywords = keywordGroup.keywords || [];
  const visibleKeywords = showAll ? keywords : keywords.slice(0, maxVisible);
  const hasMore = keywords.length > maxVisible;

  return (
    <div className="keyword-group-card">
      <div className="keyword-group-header">
        <h4 className="keyword-group-name">{keywordGroup.groupName}</h4>
        <div className="keyword-group-actions">
          <Icon
            icon="edit"
            style={{ cursor: "pointer", color: "#007bff", fontSize: "18px" }}
            onClick={() => onEdit(keywordGroup)}
          />
          <Icon
            icon="delete"
            style={{ cursor: "pointer", color: "#dc3545", fontSize: "18px" }}
            onClick={() => onDelete(keywordGroup)}
          />
        </div>
      </div>
      <div className="keyword-tags">
        {visibleKeywords.map((keyword, index) => (
          <Tag
            key={`${keyword}-${index}`}
            text={keyword}
            color="Gray-100"
            isCopyable={false}
            isRemovable={false}
            size="small"
          />
        ))}
        {hasMore && !showAll && (
          <button className="show-all-btn" onClick={() => setShowAll(true)}>
            And {keywords.length - maxVisible} more keywords
          </button>
        )}
        {showAll && hasMore && (
          <button className="show-all-btn" onClick={() => setShowAll(false)}>
            Show less
          </button>
        )}
      </div>
    </div>
  );
}
